import { HomeAdvantagesVideoConfig } from '@/js/utils/animations/home-advantages-video-config';

export function initHomeAdvantagesVideo(): void {
  const cfg = HomeAdvantagesVideoConfig;

  const section = document.querySelector(cfg.sectionSelector);
  const video = section?.querySelector<HTMLVideoElement>(cfg.videoSelector);
  if (!video) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    video.pause();
    return;
  }

  if (video.dataset.stcAdvantagesVideoPingpong === '1') return;
  video.dataset.stcAdvantagesVideoPingpong = '1';

  const { timeEpsilon, nativeReverseWatchMs, reverseStepSeconds } = cfg;

  let useRafBackward = false;
  let watchdogId: number | null = null;
  let reverseRafId: number | null = null;

  const clearWatchdog = () => {
    if (watchdogId !== null) {
      window.clearTimeout(watchdogId);
      watchdogId = null;
    }
  };

  const cancelReverseLoop = () => {
    if (reverseRafId !== null) {
      cancelAnimationFrame(reverseRafId);
      reverseRafId = null;
    }
  };

  const playForward = () => {
    clearWatchdog();
    cancelReverseLoop();
    video.playbackRate = 1;
    video.currentTime = 0;
    void video.play().catch(() => {});
  };

  const runRafBackward = () => {
    clearWatchdog();
    cancelReverseLoop();
    video.pause();
    video.playbackRate = 1;

    const duration = video.duration;
    if (!Number.isFinite(duration) || duration <= 0) {
      playForward();
      return;
    }

    // не дёргаем currentTime, если уже у конца (ended) — лишний seek даёт вспышку
    if (video.currentTime < duration - timeEpsilon) {
      video.currentTime = duration;
    }

    // Пока video.seeking — не ставим новый currentTime: иначе копится очередь seek’ов
    // и кадры «ползут» с большой задержкой.
    const tick = () => {
      if (video.seeking) {
        reverseRafId = requestAnimationFrame(tick);
        return;
      }
      const t = video.currentTime;
      let next = Math.max(0, t - reverseStepSeconds);
      if (next <= timeEpsilon) {
        cancelReverseLoop();
        video.currentTime = 0;
        playForward();
        return;
      }
      if (next >= t - 1e-4) {
        next = Math.max(0, t - 0.05);
      }
      video.currentTime = next;
      reverseRafId = requestAnimationFrame(tick);
    };

    reverseRafId = requestAnimationFrame(tick);
  };
  const tryNativeBackward = () => {
    clearWatchdog();
    cancelReverseLoop();

    const duration = video.duration;
    if (!Number.isFinite(duration) || duration <= timeEpsilon) {
      playForward();
      return;
    }

    const switchToRafBackward = () => {
      if (useRafBackward) return;
      useRafBackward = true;
      video.pause();
      video.playbackRate = 1;
      if (video.currentTime < duration - timeEpsilon) {
        video.currentTime = duration;
      }
      runRafBackward();
    };

    try {
      video.playbackRate = -1;
      video.currentTime = Math.max(timeEpsilon, duration - 0.02);
    } catch {
      switchToRafBackward();
      return;
    }

    let playPromise: Promise<void> | undefined;
    try {
      playPromise = video.play();
    } catch {
      switchToRafBackward();
      return;
    }

    void playPromise?.catch(() => {
      switchToRafBackward();
    });

    const tCheckpoint = video.currentTime;
    watchdogId = window.setTimeout(() => {
      watchdogId = null;
      if (useRafBackward) return;
      if (video.playbackRate >= 0) return;
      if (video.currentTime >= tCheckpoint - 0.08) {
        switchToRafBackward();
      }
    }, nativeReverseWatchMs);
  };

  video.addEventListener('ended', () => {
    if (useRafBackward) {
      runRafBackward();
    } else {
      tryNativeBackward();
    }
  });

  video.addEventListener('timeupdate', () => {
    if (video.playbackRate < 0 && video.currentTime < video.duration - 0.12) {
      clearWatchdog();
    }
    if (!useRafBackward && video.playbackRate < 0 && video.currentTime <= timeEpsilon) {
      clearWatchdog();
      video.pause();
      video.playbackRate = 1;
      video.currentTime = 0;
      void video.play().catch(() => {});
    }
  });

  if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
    void video.play().catch(() => {});
  } else {
    video.addEventListener(
      'loadedmetadata',
      () => {
        void video.play().catch(() => {});
      },
      { once: true },
    );
  }
}
